import { getSupportRoute } from "core/AppRoutes";
import { fetchSystemInsetPadding, getAppVersion, getMobilePlatform } from "core/javascriptChannels";
import { useEffect, useState } from "react";
import { satisfies } from "semver";

export enum Platform {
  android = "android",
  ios = "iOS",
}

export type UseMobilePlatformType = {
  platform: Platform | undefined;
  isMobileApp: boolean;
  insetPadding: Record<string, any> | undefined;
  backgroundAudioEnabled?: boolean;
  appVersion: string | undefined;
  loading: boolean;
  isAuthV2: boolean;
  supportLink: string;
};

export const useMobilePlatform = (): UseMobilePlatformType => {
  const [loading, setLoading] = useState<boolean>(true);
  const [platform, setPlatform] = useState<Platform | undefined>(undefined);
  const [appVersion, setAppVersion] = useState<string | undefined>(undefined);
  const [insetPadding, setInsetPadding] = useState<Record<string, any> | undefined>(undefined);
  const isMobileApp = window.flutter_inappwebview != null;
  const hasNewVideoVersion = appVersion && satisfies(appVersion, process.env.REACT_APP_VIDEO_CALL_VERSION!);
  const supportLink = getSupportRoute(isMobileApp && !!hasNewVideoVersion);

  // This assists backwards compatability due to fix for https://brighthr.atlassian.net/browse/MHA-649
  // where audio in the mobile app stops when screen is locked or app is in the background
  const backgroundAudioEnabled = window.backgroundAudioEnabled;

  useEffect(() => {
    async function getPlatform() {
      const platformString: string = await getMobilePlatform();
      const _appVersion: string = await getAppVersion();
      const padding = await fetchSystemInsetPadding();

      if (Object.keys(Platform).includes(platformString.toLowerCase())) {
        setPlatform((Platform as any)[platformString.toLowerCase()]);
      }
      setAppVersion(_appVersion.replace("v", ""));
      setLoading(false);
      const platformPadding =
        platformString.toLowerCase() === Platform.android
          ? Object.entries(padding).reduce((acc, [key, value]) => ({ ...acc, [key]: value + 10 }), {})
          : padding;
      padding && setInsetPadding(platformPadding);
    }
    if (isMobileApp) {
      getPlatform();
    }
  }, [isMobileApp]);

  return {
    platform,
    isMobileApp,
    insetPadding,
    backgroundAudioEnabled,
    appVersion,
    loading,
    supportLink,
    isAuthV2: !!appVersion && satisfies(appVersion, process.env.REACT_APP_NEW_AUTH_VERSION!),
  };
};
